const colors = {
  dark: '#000',
  dark2: '#222',
  dark3: '#24282B',
  dark4: '#1D2326',
  dark5: '#24282C',
  dark6: '#34393c',
  darkTransparent: '#000000a6',
  gray: '#a0a1a2',
  gray2: '#a0a1a2',
  gray3: '#9B9B9B',
  gray4: '#d1d3d5',
  gray5: '#e9e9e9',
  gray6: '#C8C9CA',
  gray7: '#D9D9D94D',
  gray8: '#0009',
  gray9: '#fdfdfe',
  gray10: '#99a1af',
  grayTransparent: '#c8c9ca1a',
  boxShadow: '#c8c9ca42',
  grayHover: '#0000000f',
  grayActive: '#00000026',
  white: '#ffffff',
  white5: '#FFFFFF0D',
}

const colorTypes = {
  primary: '#FFD500',
  primaryLight: '#FFD500',
  primary50: '#FFCC0819',
  primary100: '#FFCC0826',
  primary150: '#FFCC0833',
  primary200: '#FFCC084C',
  primary250: '#FFCC0866',
  primary300: '#FFCC087F',
  primary350: '#FFCC0899',
  primary400: '#FFCC08B2',
  primary450: '#FFCC08CC',
  primary500: '#FFCC08E5',
  primary600: '#EABC0CFF',
  primary700: '#C09C15FF',
  primary800: '#967C1EFF',
  primary900: '#6C5C26FF',
  error: '#ff4d4f',
  errorLighten: '#fef2f2',
}

const themeColors = {
  ...colors,
  ...colorTypes,
  text: colors.dark2,
  textDark: colors.white,
  textLink: colors.dark,
  textGray: colors.gray,
  textShadow: '0 0 4px black',
  titleBorder: '1px solid black',
  titleBorderDark: '1px solid rgba(255, 255, 255, 0.5)',
  foreground: colors.white,
  title: colors.dark,
  titleDark: colors.white,
  titleHover: colorTypes.primary,
  link: colors.dark2,
  linkDark: colors.white,
  bgDark: colors.dark3,
  bgContent: colors.white,
  bgFooter: colors.dark5,
  scrollBg: colors.dark3,
  scrollColor: colors.gray,
  scrollHoverColor: '#a0a1a29c',
  scrollBorderColor: colors.dark2,
  bgNav: colorTypes.primary,
  borderColor: '#00000026',
  borderColor2: colors.gray6,
  borderColor3: '#ebe6e7',
  nav: colors.dark5,
  divider: 'rgba(5, 5, 5, 0.06)',
  borderColorAlpha: 'rgba(194,194,194,0.18)',
} as const

export const sizes = {
  container: 1440,
}

interface Size {
  sm: number
  md: number
  lg: number
  xl: number
}

export const mediaSizes: Size = {
  //  Смартфон
  //  xs < 576px
  sm: 576,
  //  Планшет
  md: 768,
  //  Десктоп
  lg: 1200,
  // Широкий
  xl: sizes.container,
}

export const media = {
  sm: `(min-width: ${mediaSizes.sm}px)`,
  md: `(min-width: ${mediaSizes.md}px)`,
  lg: `(min-width: ${mediaSizes.lg}px)`,
  xl: `(min-width: ${mediaSizes.xl}px)`,
} as const

const theme = {
  colors: themeColors,
  sizes,
  mediaSizes,
  token: {
    layout: {
      headerHeight: 57,
      pagePadding: 10,
      pagePaddingDesktop: 100,
      pagePaddingX: `clamp(10px, calc(10px + (100vw - ${mediaSizes.sm}px) * 90 / ${mediaSizes.xl - mediaSizes.sm}), 100px)`,
    },
    product: {
      maxDescriptionHeight: 246,
    },
  },
}

export default theme

export type ThemeColor = (typeof theme.colors)[keyof typeof theme.colors]

/**
 --g-color-private-color-50: rgba(255,204,8,0.1);
 --g-color-private-color-100: rgba(255,204,8,0.15);
 --g-color-private-color-150: rgba(255,204,8,0.2);
 --g-color-private-color-200: rgba(255,204,8,0.3);
 --g-color-private-color-250: rgba(255,204,8,0.4);
 --g-color-private-color-300: rgba(255,204,8,0.5);
 --g-color-private-color-350: rgba(255,204,8,0.6);
 --g-color-private-color-400: rgba(255,204,8,0.7);
 --g-color-private-color-450: rgba(255,204,8,0.8);
 --g-color-private-color-500: rgba(255,204,8,0.9);

 --g-color-private-color-50-solid: rgb(255,250,230);
 --g-color-private-color-100-solid: rgb(255,247,218);
 --g-color-private-color-150-solid: rgb(255,245,206);
 --g-color-private-color-200-solid: rgb(255,240,181);
 --g-color-private-color-250-solid: rgb(255,235,156);
 --g-color-private-color-300-solid: rgb(255,230,132);
 --g-color-private-color-350-solid: rgb(255,224,107);
 --g-color-private-color-400-solid: rgb(255,219,82);
 --g-color-private-color-450-solid: rgb(255,214,57);
 --g-color-private-color-500-solid: rgb(255,209,33);
 --g-color-private-color-550-solid: rgb(255,204,8);
 --g-color-private-color-600-solid: rgb(234,188,12);
 --g-color-private-color-650-solid: rgb(213,172,17);
 --g-color-private-color-700-solid: rgb(192,156,21);
 --g-color-private-color-750-solid: rgb(171,140,25);
 --g-color-private-color-800-solid: rgb(150,124,30);
 --g-color-private-color-850-solid: rgb(129,108,34);
 --g-color-private-color-900-solid: rgb(108,92,38);
 --g-color-private-color-950-solid: rgb(87,76,42);
 --g-color-private-color-1000-solid: rgb(77,68,45);
 */
